import { memo, useEffect, useMemo, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerCandle } from '@/types'

interface PriceVolumeChartProps {
    candles: ScreenerCandle[]
    symbol: string
    exchange?: string
    onCrosshairMove?: (time: Time | null, price: number | null) => void
    syncedTime?: Time | null
    priceHeight?: number
    volumeHeight?: number
}

interface PriceDataPoint {
    tsMs: number
    open: number
    high: number
    low: number
    close: number
    volume: number
}

function formatCompact(value: number): string {
    const abs = Math.abs(value)
    if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
    if (abs >= 1_000) return `${(value / 1_000).toFixed(2)}K`
    return value.toFixed(2)
}

function PriceVolumeChartComponent({
    candles,
    symbol: _symbol,
    exchange,
    onCrosshairMove,
    syncedTime,
    priceHeight = 420,
    volumeHeight = 80,
}: PriceVolumeChartProps) {
    const chartRef = useRef<ReactECharts>(null)
    const totalHeight = priceHeight + volumeHeight + 30 // 30 for x-axis labels

    const derived = useMemo(() => {
        const sorted = [...candles].sort((a, b) => a.timestamp - b.timestamp)
        const series: PriceDataPoint[] = sorted.map(c => ({
            tsMs: c.timestamp * 1000,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            volume: c.volume,
        }))
        const tsMs = series.map(item => item.tsMs)
        return { series, tsMs }
    }, [candles])

    const nearestIndex = useMemo(() => {
        const values = derived.tsMs
        return (target: number): number => {
            if (values.length === 0) return -1
            let left = 0
            let right = values.length - 1
            while (left <= right) {
                const mid = (left + right) >> 1
                const value = values[mid]
                if (value === target) return mid
                if (value < target) left = mid + 1
                else right = mid - 1
            }
            if (left >= values.length) return values.length - 1
            if (left <= 0) return 0
            const prev = values[left - 1]
            const next = values[left]
            return Math.abs(prev - target) <= Math.abs(next - target) ? left - 1 : left
        }
    }, [derived.tsMs])

    // Compute price grid percentage heights
    const priceGridHeight = `${((priceHeight / totalHeight) * 100).toFixed(1)}%`
    const volumeGridTop = `${(((priceHeight + 8) / totalHeight) * 100).toFixed(1)}%`
    const volumeGridHeight = `${((volumeHeight / totalHeight) * 100).toFixed(1)}%`

    const option = useMemo<EChartsOption>(() => {
        const candleData = derived.series.map(c => [c.tsMs, c.open, c.close, c.low, c.high])
        const volumeData = derived.series.map(c => [c.tsMs, c.volume])

        return {
            animation: false,
            backgroundColor: 'transparent',
            textStyle: { color: '#d1d4dc' },
            grid: [
                // Price grid
                { left: 8, right: 8, top: 30, height: priceGridHeight, containLabel: true },
                // Volume grid
                { left: 8, right: 8, top: volumeGridTop, height: volumeGridHeight, containLabel: true },
            ],
            axisPointer: {
                link: [{ xAxisIndex: [0, 1] }],
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                borderColor: '#374151',
                textStyle: { color: '#e5e7eb', fontSize: 11 },
                formatter: (params: any) => {
                    const list = Array.isArray(params) ? params : [params]
                    const candleEntry = list.find((p: any) => p.seriesName === 'Price')
                    const volEntry = list.find((p: any) => p.seriesName === 'Volume')
                    const rawData = candleEntry?.data as number[] | undefined
                    const axisMs = Number(candleEntry?.axisValue ?? volEntry?.axisValue ?? 0)

                    if (!rawData || rawData.length < 5) {
                        return `<div style="color:#9ca3af;">${new Date(axisMs).toLocaleString()}</div>`
                    }

                    const open = Number(rawData[1])
                    const close = Number(rawData[2])
                    const low = Number(rawData[3])
                    const high = Number(rawData[4])
                    const change = open !== 0 ? ((close - open) / open) * 100 : 0
                    const changeColor = change >= 0 ? '#26a69a' : '#ef5350'
                    const vol = volEntry?.data?.[1]

                    const formatPrice = (value: number) =>
                        value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })

                    return `
            <div style="color:#9ca3af;margin-bottom:6px;">${new Date(axisMs).toLocaleString()}</div>
            <div>O: <span style="color:#f3f4f6;font-weight:600;">$${formatPrice(open)}</span></div>
            <div>H: <span style="color:#26a69a;font-weight:600;">$${formatPrice(high)}</span></div>
            <div>L: <span style="color:#ef5350;font-weight:600;">$${formatPrice(low)}</span></div>
            <div>C: <span style="color:#f3f4f6;font-weight:600;">$${formatPrice(close)}</span></div>
            <div>Chg: <span style="color:${changeColor};font-weight:600;">${change >= 0 ? '+' : ''}${change.toFixed(2)}%</span></div>
            ${vol != null ? `<div>Vol: <span style="color:#60a5fa;font-weight:600;">$${formatCompact(Number(vol))}</span></div>` : ''}
          `
                },
            },
            xAxis: [
                {
                    type: 'time',
                    gridIndex: 0,
                    axisLine: { lineStyle: { color: '#1e222d' } },
                    axisLabel: { show: false },
                    axisTick: { show: false },
                    splitLine: { lineStyle: { color: '#1e222d', type: 'dashed' } },
                },
                {
                    type: 'time',
                    gridIndex: 1,
                    axisLine: { lineStyle: { color: '#1e222d' } },
                    axisLabel: {
                        color: '#787b86',
                        fontSize: 10,
                        hideOverlap: true,
                    },
                    splitLine: { show: false },
                },
            ],
            yAxis: [
                {
                    type: 'value',
                    gridIndex: 0,
                    position: 'right',
                    axisLine: { show: false },
                    splitLine: { lineStyle: { color: '#1e222d', type: 'dashed' } },
                    axisLabel: {
                        color: '#787b86',
                        fontSize: 10,
                        formatter: (value: number) => {
                            if (value >= 1000) return formatCompact(value)
                            return value.toLocaleString(undefined, { maximumFractionDigits: 6 })
                        },
                    },
                },
                {
                    type: 'value',
                    gridIndex: 1,
                    position: 'right',
                    axisLine: { show: false },
                    splitLine: { show: false },
                    axisLabel: {
                        color: '#787b86',
                        fontSize: 9,
                        formatter: (value: number) => formatCompact(value),
                    },
                },
            ],
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: [0, 1],
                    zoomOnMouseWheel: true,
                    moveOnMouseWheel: true,
                    moveOnMouseMove: true,
                    preventDefaultMouseMove: false,
                    filterMode: 'none',
                },
                {
                    type: 'inside',
                    yAxisIndex: [0],
                    zoomOnMouseWheel: 'shift',
                    moveOnMouseMove: 'shift',
                    moveOnMouseWheel: false,
                    preventDefaultMouseMove: false,
                    filterMode: 'none',
                },
                {
                    type: 'slider',
                    xAxisIndex: [0, 1],
                    show: false,
                    start: 0,
                    end: 100,
                    filterMode: 'none',
                },
            ],
            series: [
                {
                    name: 'Price',
                    type: 'candlestick',
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                    itemStyle: {
                        color: '#26a69a',
                        color0: '#ef5350',
                        borderColor: '#26a69a',
                        borderColor0: '#ef5350',
                    },
                    data: candleData,
                },
                {
                    name: 'Volume',
                    type: 'bar',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    barMaxWidth: 8,
                    itemStyle: {
                        color: (params: any) => {
                            const item = derived.series[params.dataIndex]
                            if (!item) return 'rgba(96, 165, 250, 0.5)'
                            return item.close >= item.open ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)'
                        },
                    },
                    data: volumeData,
                },
            ],
        }
    }, [derived, priceGridHeight, volumeGridTop, volumeGridHeight])

    useEffect(() => {
        if (!onCrosshairMove) return
        const instance = chartRef.current?.getEchartsInstance()
        if (!instance) return

        const handler = (event: any) => {
            const axisInfo = event?.axesInfo?.[0]
            if (!axisInfo) return
            const axisMs = Number(axisInfo.value)
            if (!Number.isFinite(axisMs)) return

            const idx = nearestIndex(axisMs)
            const point = idx >= 0 ? derived.series[idx] : null
            onCrosshairMove(Math.floor(axisMs / 1000) as Time, point?.close ?? null)
        }
        const outHandler = () => onCrosshairMove(null, null)

        instance.on('updateAxisPointer', handler)
        instance.on('mouseout', outHandler)
        return () => {
            instance.off('updateAxisPointer', handler)
            instance.off('mouseout', outHandler)
        }
    }, [onCrosshairMove, derived.series, nearestIndex])

    useEffect(() => {
        const instance = chartRef.current?.getEchartsInstance()
        if (!instance) return
        if (syncedTime === undefined) return

        if (syncedTime === null) {
            instance.dispatchAction({ type: 'hideTip' })
            return
        }

        const targetMs = Number(syncedTime) * 1000
        if (!Number.isFinite(targetMs) || derived.tsMs.length === 0) return

        const idx = nearestIndex(targetMs)
        if (idx >= 0) {
            instance.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: idx })
        }
    }, [syncedTime, derived.tsMs, nearestIndex])

    // Current price info
    const lastCandle = derived.series.length > 0 ? derived.series[derived.series.length - 1] : null
    const prevCandle = derived.series.length > 1 ? derived.series[derived.series.length - 2] : null
    const currentPrice = lastCandle?.close ?? null
    const priceChange = currentPrice && prevCandle ? ((currentPrice - prevCandle.close) / prevCandle.close) * 100 : 0
    const priceColor = priceChange >= 0 ? '#26a69a' : '#ef5350'
    const currentVolume = lastCandle?.volume ?? null

    return (
        <div className="relative">
            {/* Price label overlay */}
            <div className="absolute top-1.5 left-2 z-10 flex items-center gap-3">
                <span className="text-[10px] font-medium text-[#787b86] uppercase tracking-wide">
                    Price {exchange && <span className="text-[#545760]">({exchange})</span>}
                </span>
                {currentPrice !== null && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#d1d4dc]">
                            ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                        </span>
                        <span className="text-xs font-medium" style={{ color: priceColor }}>
                            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                        </span>
                    </div>
                )}
            </div>

            {/* Volume label overlay */}
            <div
                className="absolute left-2 z-10 flex items-center gap-2"
                style={{ top: priceHeight + 14 }}
            >
                <span className="text-[9px] font-medium text-[#787b86] uppercase tracking-wide">
                    Volume
                </span>
                {currentVolume !== null && (
                    <span className="text-[10px] text-[#545760]">{formatCompact(currentVolume)}</span>
                )}
            </div>

            <ReactECharts
                ref={chartRef}
                option={option}
                style={{ height: totalHeight, width: '100%' }}
                notMerge
                lazyUpdate
            />
        </div>
    )
}

export const PriceVolumeChart = memo(PriceVolumeChartComponent)
